import React from "react";
import { useForm } from "react-hook-form";
import { Review } from "../../../types";
import { ReviewFormProps, ReviewFormData } from "../types/reviews-types";
import { ControlledTextField } from "../../../components/form";
import { ControlledSelect } from "../../../components/form";
import { ControlledTextarea } from "../../../components/form";
import { RATING_OPTIONS, SOURCE_OPTIONS } from "../../../constants/forms";

export const ReviewForm: React.FC<ReviewFormProps> = ({
  onSubmit,
  initialValues,
  isLoading = false,
}) => {
  // Format date from ISO to YYYY-MM-DD for date input
  const formatDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return "";
    try {
      // If the date string is already in YYYY-MM-DD format, return it
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      // If it's an ISO string, extract the date portion directly without timezone conversion
      if (dateString.includes("T")) {
        return dateString.split("T")[0] || "";
      }
      // Otherwise, try to extract the first 10 characters (YYYY-MM-DD)
      return dateString.substring(0, 10) || "";
    } catch {
      return "";
    }
  };

  const form = useForm<ReviewFormData>({
    defaultValues: {
      customerName: initialValues?.customerName || "",
      rating: initialValues?.rating || 5,
      reviewDate: formatDateForInput(initialValues?.reviewDate),
      comment: initialValues?.content || "",
      source: initialValues?.source || "",
      sourceUrl: initialValues?.sourceUrl || "",
      isApproved: initialValues?.isApproved ?? true,
      isPublished: initialValues?.isPublished ?? true,
    },
  });

  const handleFormSubmit = async (data: ReviewFormData): Promise<void> => {
    // Validate rating range
    const rating = Number(data.rating);
    if (isNaN(rating) || rating < 1 || rating > 5) {
      console.error("Invalid rating value:", data.rating);
      return;
    }

    const reviewData: Partial<Review> = {
      customerName: data.customerName,
      rating: rating,
      reviewDate: data.reviewDate,
      content: data.comment,
      source: data.source,
      ...(data.sourceUrl && { sourceUrl: data.sourceUrl }),
      ...(data.isApproved !== undefined && { isApproved: data.isApproved }),
      ...(data.isPublished !== undefined && { isPublished: data.isPublished }),
    };
    await onSubmit(reviewData);
  };

  return (
    <form
      onSubmit={form.handleSubmit(handleFormSubmit)}
      className="form-space-y-2"
    >
      <ControlledTextField
        control={form.control}
        name="customerName"
        label="Имя клиента"
        placeholder="Введите имя клиента"
        required
        disabled={isLoading}
      />

      <ControlledSelect
        control={form.control}
        name="rating"
        label="Рейтинг"
        placeholder="Выберите рейтинг"
        required
        disabled={isLoading}
        options={RATING_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
      />

      <ControlledTextField
        control={form.control}
        name="reviewDate"
        type="date"
        label="Дата отзыва"
        placeholder="Выберите дату"
        required
        disabled={isLoading}
      />

      <ControlledTextarea
        control={form.control}
        name="comment"
        label="Содержание отзыва"
        placeholder="Введите текст отзыва"
        required
        disabled={isLoading}
        rows={4}
      />

      <ControlledSelect
        control={form.control}
        name="source"
        label="Источник"
        placeholder="Выберите источник"
        required
        disabled={isLoading}
        options={SOURCE_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
      />

      <ControlledTextField
        control={form.control}
        name="sourceUrl"
        type="url"
        label="Ссылка на отзыв"
        placeholder="Введите ссылку на отзыв"
        disabled={isLoading}
      />

      <div className="form-wrapper__actions">
        <button
          type="submit"
          className="form-button form-button--primary form-button--full"
          disabled={isLoading || !form.formState.isValid}
        >
          {isLoading
            ? "Сохранение..."
            : initialValues
            ? "Сохранить изменения"
            : "Добавить отзыв"}
        </button>
      </div>
    </form>
  );
};
